/**
 * Shared formatting utilities for the Order Detail page and its sub-components.
 */

/**
 * Format cents to dollar display string
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted currency string (e.g., "$123.45")
 */
export const formatCurrency = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

/**
 * Copy text to clipboard with optional label for logging
 * @param {string} text - Text to copy
 * @param {string} label - Label for console log
 */
export const copyToClipboard = (text, label) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      console.log(`${label || "Text"} copied to clipboard`);
    })
    .catch((err) => {
      console.error("Failed to copy:", err);
    });
};

/**
 * Format a date string to "MM/DD/YYYY at H:MM AM/PM"
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date/time or "N/A"
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return (
    date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }) +
    " at " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  );
};

/**
 * Format a date string to "MMM DD, YYYY" (short format)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or "N/A"
 */
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Generate 15-minute interval time options for the day (00:00 - 23:45)
 */
export const timeOptions = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const value = `${hh}:${mm}`;
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? "AM" : "PM";
      const label = `${hour12}:${mm} ${ampm}`;
      opts.push({ value, label });
    }
  }
  return opts;
})();

/**
 * Convert 24h time value to 12h display label
 * @param {string} val - "HH:MM" format
 * @returns {string} "H:MM AM/PM" format
 */
export const formatTime12 = (val) => {
  if (!val) return "";
  const found = timeOptions.find((o) => o.value === val);
  return found ? found.label : val;
};

/**
 * Preset tag colors used across tag management UI
 */
// Ordered around the spectrum (red → pink) so the picker reads as a clean
// rainbow. PRESET_COLORS_LIGHT is the same 16 hues, much lighter (pastel) and
// index-aligned 1:1 — the tag editor renders them as a second row beneath the
// full colors.
export const PRESET_COLORS = [
  { name: "Red", color: "#ef4444" },
  { name: "Orange", color: "#f97316" },
  { name: "Amber", color: "#f59e0b" },
  { name: "Yellow", color: "#eab308" },
  { name: "Lime", color: "#84cc16" },
  { name: "Green", color: "#22c55e" },
  { name: "Emerald", color: "#10b981" },
  { name: "Teal", color: "#14b8a6" },
  { name: "Cyan", color: "#06b6d4" },
  { name: "Sky", color: "#0ea5e9" },
  { name: "Blue", color: "#3b82f6" },
  { name: "Indigo", color: "#6366f1" },
  { name: "Violet", color: "#8b5cf6" },
  { name: "Purple", color: "#a855f7" },
  { name: "Fuchsia", color: "#d946ef" },
  { name: "Pink", color: "#ec4899" },
];

export const PRESET_COLORS_LIGHT = [
  { name: "Light Red", color: "#fecaca" },
  { name: "Light Orange", color: "#fed7aa" },
  { name: "Light Amber", color: "#fde68a" },
  { name: "Light Yellow", color: "#fef08a" },
  { name: "Light Lime", color: "#d9f99d" },
  { name: "Light Green", color: "#bbf7d0" },
  { name: "Light Emerald", color: "#a7f3d0" },
  { name: "Light Teal", color: "#99f6e4" },
  { name: "Light Cyan", color: "#a5f3fc" },
  { name: "Light Sky", color: "#bae6fd" },
  { name: "Light Blue", color: "#bfdbfe" },
  { name: "Light Indigo", color: "#c7d2fe" },
  { name: "Light Violet", color: "#ddd6fe" },
  { name: "Light Purple", color: "#e9d5ff" },
  { name: "Light Fuchsia", color: "#f5d0fe" },
  { name: "Light Pink", color: "#fbcfe8" },
];

// Neutral default for one-time typed tags — deliberately outside the rainbow so
// they read as visually distinct from the configured saved tags.
export const NEUTRAL_TAG_COLOR = "#94a3b8";
